/**
 * BackLink - Standardized "← Back to …" link rendered at the top of
 * detail and create pages.
 *
 * Atomic Design role: Atom. Pure presentational button — the caller
 * passes the destination href or an onClick. Centralizes the chevron
 * icon, hover state, and spacing so every detail page back-link looks
 * identical (previously seven different pages each rendered the same
 * markup inline).
 *
 * Prefer the `to` prop for static routes; use `onClick` when the back
 * action is something other than route navigation (e.g.
 * window.history.back() in a sub-overlay).
 *
 * @example
 * <BackLink to="/work-orders" label="Back to Work Orders" />
 * <BackLink onClick={() => window.history.back()} label="Back" />
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';

export interface BackLinkProps {
	/** Route to navigate to. Mutually exclusive with onClick. */
	to?: string;
	/** Custom click handler. Mutually exclusive with `to`. */
	onClick?: () => void;
	/** Label rendered after the chevron. Defaults to "Back". */
	label?: string;
	/** Optional extra classes merged onto the button. */
	className?: string;
}

export const BackLink: React.FC<BackLinkProps> = ({ to, onClick, label = 'Back', className = '' }) => {
	const navigate = useNavigate();
	const handleClick = () => {
		if (onClick) {
			onClick();
			return;
		}
		if (to) {
			navigate(to);
		}
	};
	return (
		<button
			type="button"
			onClick={handleClick}
			data-ref="atom-back-link"
			/* The label alone is only ~21px tall — under the 44px touch floor, and
			   it sits at the very top of the page where a mis-tap is most likely.
			   min-h-44px grows the hit area on touch viewports without moving the
			   text; desktop keeps the tight original height. */
			className={`back-link inline-flex items-center gap-4px text-14px font-sans text-brand-text-muted hover:text-brand-primary mb-16px min-h-44px desktop:min-h-0 transition-colors ${className}`}
		>
			<svg className="back-link__icon h-16px w-16px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
				<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
			</svg>
			<span className="back-link__label">{label}</span>
		</button>
	);
};

export default BackLink;
